import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BarChart3, Loader2, Lock, UserPen, Users, Wrench } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EnrichmentWork } from "@/components/enrichment/EnrichmentWork";
import { EnrichmentCorrections } from "@/components/enrichment/EnrichmentCorrections";
import { EnrichmentStats } from "@/components/enrichment/EnrichmentStats";
import { EnrichmentTeamStats } from "@/components/enrichment/EnrichmentTeamStats";
import { ActiveSessionsPanel } from "@/components/enrichment/ActiveSessionsPanel";
import { CfoEnrichmentStats } from "@/components/enrichment/cfo/CfoEnrichmentStats";
import { CfoEnrichmentWork } from "@/components/enrichment/cfo/CfoEnrichmentWork";
import { UsGrowthQueue } from "@/components/enrichment/cfo/UsGrowthQueue";
import { FeatureDisabled } from "@/components/layout/FeatureDisabled";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useEnrichmentQueueCounts } from "@/hooks/useEnrichmentQueueCounts";
import { useFeature } from "@/hooks/useFeature";
import { usePermissions } from "@/hooks/usePermissions";

type Tab = "growth" | "work" | "corrections" | "stats" | "payouts" | "sessions";

/** Operator workstation with server-scoped performance and payouts.
 *  Managers see the team; operators see their own work. */
export default function EnrichmentPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { canViewEnrichmentStats, canManageEnrichmentRates } = usePermissions();
  const enabled = useFeature("enrichment");
  const { project } = useCurrentProject();
  // A CFO project's enrichment is its CFO research: the companies whose
  // decision waits for a person. Corrections and payouts are M&A work.
  const isCfo =
    project?.project_type_id === "cfo" || project?.project_type?.id === "cfo";
  const [tab, setTab] = useState<Tab>("growth");

  // One read for both queues, owned here because both queue tabs are
  // mounted at once and each only needs its own number out of it.
  const { counts, refresh: refreshQueueCounts } = useEnrichmentQueueCounts(
    slug,
    enabled,
  );

  useEffect(() => {
    if (tab === "growth" && project?.slug === slug && !isCfo) setTab("work");
    if (tab === "stats" && !canViewEnrichmentStats) setTab("work");
    if (tab === "payouts" && (!canViewEnrichmentStats || isCfo)) setTab("work");
    if (tab === "corrections" && isCfo) setTab("work");
    if (tab === "sessions" && !canManageEnrichmentRates) setTab("work");
  }, [
    tab,
    canViewEnrichmentStats,
    canManageEnrichmentRates,
    isCfo,
    project?.slug,
    slug,
  ]);

  if (!enabled) {
    return <FeatureDisabled icon={UserPen} title="Enrichment is disabled" />;
  }

  // Which workstation depends on the project's type: nothing is mounted (and
  // no lot reserved) before the project of this page is known.
  if (project?.slug !== slug) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (isCfo) {
    return (
      <div className="space-y-6 pb-24">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Enrichment
            </h1>
            <p className="text-sm text-slate-500">
              Find the data a CFO decision waits for
            </p>
          </div>
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              <TabsTrigger value="growth">US Growth</TabsTrigger>
              <TabsTrigger value="work">
                <UserPen className="mr-1.5 h-4 w-4" />
                Research
              </TabsTrigger>
              {canViewEnrichmentStats && (
                <TabsTrigger value="stats">
                  <BarChart3 className="mr-1.5 h-4 w-4" />
                  Stats
                </TabsTrigger>
              )}
              {canManageEnrichmentRates && (
                <TabsTrigger value="sessions">
                  <Lock className="mr-1.5 h-4 w-4" />
                  Sessions
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
        </header>

        {/* The research tab stays mounted, like the M&A queues: it holds a
         *  reserved lot and a cursor into it. */}
        {tab === "growth" && <UsGrowthQueue key={slug} slug={slug} />}
        <div className={tab === "work" ? "" : "hidden"}>
          <CfoEnrichmentWork
            slug={slug}
            active={tab === "work"}
            counts={counts?.cfo ?? null}
            onQueueChanged={refreshQueueCounts}
          />
        </div>
        {tab === "stats" && <CfoEnrichmentStats slug={slug} />}
        {tab === "sessions" && <ActiveSessionsPanel slug={slug} />}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Enrichment
          </h1>
          <p className="text-sm text-slate-500">
            Fill in contact info for qualified prospects
          </p>
        </div>
        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
          <TabsList>
            <TabsTrigger value="work">
              <UserPen className="mr-1.5 h-4 w-4" />
              Work
            </TabsTrigger>
            <TabsTrigger value="corrections">
              <Wrench className="mr-1.5 h-4 w-4" />
              Corrections
            </TabsTrigger>
            {canViewEnrichmentStats && (
              <TabsTrigger value="stats">
                <BarChart3 className="mr-1.5 h-4 w-4" />
                Stats &amp; Earnings
              </TabsTrigger>
            )}
            {/* Gated on the same permission the endpoint requires, and
             *  nothing more: whether it answers with the whole team or
             *  with the caller's own line is decided server-side. */}
            {canViewEnrichmentStats && (
              <TabsTrigger value="payouts">
                <Users className="mr-1.5 h-4 w-4" />
                Payouts
              </TabsTrigger>
            )}
            {canManageEnrichmentRates && (
              <TabsTrigger value="sessions">
                <Lock className="mr-1.5 h-4 w-4" />
                Sessions
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
      </header>

      {/* Both queue tabs stay mounted; only visibility toggles. Each holds
       *  a reserved batch and a cursor into it, so unmounting on a tab
       *  switch would drop the operator back to the setup screen and
       *  strand rows nobody is working. Same reasoning, and the same
       *  shape, as the Call Center's tabs.
       *
       *  Stats, Payouts and Sessions are read-only views that fetch on
       *  mount, so they stay conditional — there is no state to lose, and
       *  mounting them eagerly would cost a request per page load. */}
      <div className={tab === "work" ? "" : "hidden"}>
        <EnrichmentWork
          slug={slug}
          active={tab === "work"}
          selectionCounts={counts?.work_selection}
          queueCount={counts?.work ?? null}
          onQueueChanged={refreshQueueCounts}
        />
      </div>
      <div className={tab === "corrections" ? "" : "hidden"}>
        <EnrichmentCorrections
          slug={slug}
          active={tab === "corrections"}
          queueCount={counts?.corrections ?? null}
          onQueueChanged={refreshQueueCounts}
        />
      </div>
      {tab === "stats" && <EnrichmentStats slug={slug} />}
      {tab === "payouts" && <EnrichmentTeamStats slug={slug} />}
      {tab === "sessions" && <ActiveSessionsPanel slug={slug} />}
    </div>
  );
}
